/**
 * PrivacyPolicy.jsx
 *
 * Public standalone page at /privacy — displays the AIDentalBook Privacy Policy.
 * This is a public route (no authentication required) matching the POC design:
 * - Full-page pink gradient background
 * - Max-width 720px centered container
 * - "AIDentalBook" branded link back to /
 * - White article card with 24px border-radius
 * - 18 numbered sections with Manrope headings + Inter body text
 * - Contact section with address and email
 * - Copyright footer
 *
 * Content is sourced from the existing DentalBookPrivacyPolicy component
 * and restructured into the new visual design format.
 *
 * @route /privacy (public)
 */
import {Link} from 'react-router';
import './PrivacyPolicy.css';

/* ================================================================
 * Privacy policy section data
 * Each section: { title: string, content: JSX | string[] }
 * This structure enables consistent rendering and future i18n support.
 * ================================================================ */
const SECTIONS = [
  {
    title: 'Information We Collect',
    content: (
      <>
        <p>Depending on how you use our Services, we may collect:</p>
        <ul>
          <li>Name, contact information, and account information</li>
          <li>Dental and health information provided by you or your dental providers</li>
          <li>Dental X-rays, photographs, CBCT images, intraoral scans, and digital dental 3D models</li>
          <li>Treatment, appointment, insurance, payment, and dental history information</li>
          <li>Information you upload, submit, or store in your account</li>
          <li>Device, IP address, login, security, and technical information</li>
          <li>Information necessary to provide customer support and operate our Services</li>
        </ul>
        <p>
          We collect information directly from you, from participating dental providers, and through
          authorized service providers.
        </p>
      </>
    ),
  },
  {
    title: 'Protected Health Information (PHI)',
    content: (
      <>
        <p>
          When AIDentalBook provides services involving protected health information (&ldquo;PHI&rdquo;) on
          behalf of a HIPAA-covered dental provider, we handle PHI in accordance with applicable HIPAA
          requirements and our agreements with the provider.
        </p>
        <p>We use and disclose PHI only as permitted or required by law, including for:</p>
        <ul>
          <li>Treatment and dental care</li>
          <li>Payment and insurance activities</li>
          <li>Health care operations</li>
          <li>Appointment and service communications</li>
          <li>Providing requested services</li>
          <li>Legal and regulatory requirements</li>
          <li>Other purposes authorized by applicable law</li>
        </ul>
        <p>We do not sell PHI.</p>
      </>
    ),
  },
  {
    title: 'How We Use Information',
    content: (
      <>
        <p>We may use information to:</p>
        <ul>
          <li>Provide, maintain, and improve the Services</li>
          <li>Store and manage dental records</li>
          <li>Facilitate communication between patients and dental professionals</li>
          <li>Provide AI-assisted tools and analysis where offered</li>
          <li>Process appointments, payments, insurance, and account services</li>
          <li>Provide customer support</li>
          <li>Protect the security of our Services</li>
          <li>Detect fraud, abuse, and unauthorized activity</li>
          <li>Comply with legal and regulatory requirements</li>
        </ul>
        <p>
          Where information is used for product development, analytics, research, or improvement, we will use
          de-identified or aggregated information whenever reasonably possible.
        </p>
      </>
    ),
  },
  {
    title: 'AI and Automated Services',
    content: (
      <>
        <p>
          AIDentalBook may provide AI-assisted dental information, analysis, organization, or other automated
          features.
        </p>
        <p>
          AI-generated information is intended to support, not replace, the judgment of qualified dental or
          medical professionals. Patients should consult an appropriate health care professional before making
          treatment decisions.
        </p>
        <p>
          Where PHI is used by an AI or technology service provider on behalf of a covered entity, appropriate
          contractual, security, and HIPAA requirements will apply.
        </p>
      </>
    ),
  },
  {
    title: 'Service Providers and Business Associates',
    content: (
      <>
        <p>We may use trusted third-party companies to provide services such as:</p>
        <ul>
          <li>Cloud hosting and data storage</li>
          <li>Security and authentication</li>
          <li>Communications</li>
          <li>Payment processing</li>
          <li>Technical support</li>
          <li>Analytics and system operations</li>
        </ul>
        <p>
          When required by HIPAA, we enter into appropriate Business Associate Agreements (BAAs) and require
          applicable safeguards for PHI.
        </p>
      </>
    ),
  },
  {
    title: 'Cookies and Online Tracking',
    content: (
      <>
        <p>We may use cookies and similar technologies to operate, secure, and improve our Services.</p>
        <p>
          AIDentalBook does not knowingly use PHI for targeted advertising or behavioral advertising, and we
          do not permit advertising networks to receive PHI for such purposes.
        </p>
        <p>
          Tracking technologies used within authenticated patient areas will be limited to purposes such as
          security, authentication, fraud prevention, system operation, and permitted analytics.
        </p>
        <p>
          Any use of tracking technologies involving PHI will be reviewed for applicable HIPAA requirements.
        </p>
      </>
    ),
  },
  {
    title: 'Marketing and Advertising',
    content: (
      <>
        <p>AIDentalBook does not sell your PHI.</p>
        <p>
          We do not use PHI for targeted advertising without the authorization or other legal basis required
          by applicable law.
        </p>
        <p>
          Where HIPAA requires a written authorization for marketing or another use or disclosure of PHI, we
          will obtain that authorization.
        </p>
      </>
    ),
  },
  {
    title: 'Sharing Information',
    content: (
      <>
        <p>We may disclose information:</p>
        <ul>
          <li>To your dental or health care providers at your direction or as permitted by law</li>
          <li>To service providers necessary to operate the Services</li>
          <li>When required by law, court order, or governmental authority</li>
          <li>To protect the rights, safety, and security of AIDentalBook, users, or others</li>
          <li>
            In connection with a merger, acquisition, financing, or sale of assets, subject to applicable
            privacy requirements
          </li>
        </ul>
        <p>We will limit PHI disclosures as required by applicable law.</p>
      </>
    ),
  },
  {
    title: 'Your Privacy Rights',
    content: (
      <>
        <p>Depending on applicable law, you may have rights to:</p>
        <ul>
          <li>Access and obtain a copy of your health information</li>
          <li>Request correction of your information</li>
          <li>Request restrictions on certain uses or disclosures</li>
          <li>Request confidential communications</li>
          <li>Request an accounting of certain disclosures</li>
          <li>Receive a copy of applicable privacy notices</li>
          <li>Withdraw certain permissions where legally permitted</li>
        </ul>
        <p>To exercise applicable rights, contact us using the information below.</p>
      </>
    ),
  },
  {
    title: 'Security',
    content: (
      <>
        <p>
          We maintain administrative, technical, and physical safeguards designed to protect information
          against unauthorized access, use, disclosure, alteration, or destruction.
        </p>
        <p>Security measures may include:</p>
        <ul>
          <li>Access controls and authentication</li>
          <li>Encryption where appropriate</li>
          <li>Audit logging and monitoring</li>
          <li>Backup and disaster recovery</li>
          <li>Security risk assessments</li>
          <li>Workforce security and training</li>
          <li>Incident response procedures</li>
        </ul>
        <p>No electronic system can guarantee absolute security.</p>
      </>
    ),
  },
  {
    title: 'Data Retention',
    content: (
      <>
        <p>
          We retain information for as long as reasonably necessary to provide the Services, meet contractual
          and legal obligations, maintain appropriate records, resolve disputes, and protect our legitimate
          interests.
        </p>
        <p>
          Health records may be retained according to applicable health care record-retention requirements.
        </p>
      </>
    ),
  },
  {
    title: 'Children and Minors',
    content: (
      <>
        <p>
          The Services may be used by minors when permitted by applicable law and when appropriately
          authorized or managed by a parent, guardian, or authorized representative.
        </p>
        <p>Privacy rights relating to minors may vary depending on applicable federal and state law.</p>
      </>
    ),
  },
  {
    title: 'California Privacy Rights',
    content: (
      <>
        <p>
          California residents may have additional rights under applicable California privacy laws, including
          rights relating to access, correction, deletion, and information about the collection and use of
          personal information.
        </p>
        <p>
          Certain health information may be subject to exemptions or separate requirements under HIPAA and
          other health privacy laws.
        </p>
      </>
    ),
  },
  {
    title: 'Third-Party Websites and Services',
    content: (
      <>
        <p>
          Our Services may contain links or integrations to third-party websites, applications, or services.
        </p>
        <p>
          Those third parties may have their own privacy policies and practices. AIDentalBook is not
          responsible for the privacy practices of third-party services that we do not control.
        </p>
      </>
    ),
  },
  {
    title: 'International Users',
    content: (
      <>
        <p>
          Information may be processed or stored in the United States or other locations where AIDentalBook or
          its service providers operate.
        </p>
        <p>
          By using the Services, you understand that information may be processed in accordance with
          applicable law and this Privacy Policy.
        </p>
      </>
    ),
  },
  {
    title: 'HIPAA Notice of Privacy Practices',
    content: (
      <>
        <p>
          If AIDentalBook provides Services as a Business Associate to a HIPAA-covered dental provider, the
          provider&rsquo;s Notice of Privacy Practices may contain additional information concerning how that
          provider uses and discloses your PHI.
        </p>
        <p>
          This Privacy Policy does not replace a covered provider&rsquo;s legally required HIPAA Notice of
          Privacy Practices.
        </p>
      </>
    ),
  },
  {
    title: 'Changes to This Policy',
    content: (
      <>
        <p>We may update this Privacy Policy from time to time.</p>
        <p>
          When we make material changes, we will post the updated policy and revise the &ldquo;Last
          Updated&rdquo; date. Additional notice will be provided when required by law.
        </p>
      </>
    ),
  },
  {
    title: 'Contact Us',
    content: (
      <>
        <p>For privacy questions, requests, or concerns:</p>
        <address className="pp-address">
          <span>Agape MedTecs Inc.</span>
          <span>10440 S. De Anza Blvd., Suite D4</span>
          <span>Cupertino, CA 95014</span>
        </address>
        <p>
          You may also have the right to submit a complaint to the U.S. Department of Health and Human
          Services, Office for Civil Rights, if you believe your HIPAA privacy rights have been violated. We
          will not retaliate against you for filing a privacy complaint.
        </p>
        <p>
          Email:{' '}
          <a href="mailto:privacy@aidentalbook.com" className="pp-email-link">
            privacy@aidentalbook.com
          </a>
        </p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <main className="pp-page">
      <div className="pp-container">
        {/* Branded link back to home */}
        <Link to="/" className="pp-brand">
          AIDentalBook
        </Link>

        {/* Privacy policy article card */}
        <article className="pp-card">
          {/* Title and effective date */}
          <h1 className="pp-title">Web and Mobile Privacy Policy</h1>
          <p className="pp-date">
            Effective September 9, 2026
            <span aria-hidden="true"> · </span>
            Last updated September 9, 2026
          </p>

          {/* Introductory paragraphs */}
          <div className="pp-intro">
            <p>
              Agape MedTecs Inc. (&ldquo;Agape MedTecs,&rdquo; &ldquo;AIDentalBook,&rdquo; &ldquo;we,&rdquo;
              &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your privacy and is committed to protecting
              your personal and dental health information.
            </p>
            <p>
              This Privacy Policy applies to the AIDentalBook website, iDentalBook patient application, mobile
              applications, and related services (collectively, the &ldquo;Services&rdquo;).
            </p>
          </div>

          {/* Numbered sections */}
          {SECTIONS.map((section, index) => (
            <section key={index} className="pp-section">
              <h2 className="pp-section-title">
                {index + 1}. {section.title}
              </h2>
              <div className="pp-section-body">{section.content}</div>
            </section>
          ))}

          {/* Copyright footer */}
          <p className="pp-footer">&copy; 2026 Agape MedTecs Inc. All rights reserved.</p>
        </article>
      </div>
    </main>
  );
}
